import {
  MultimodalAssetDto,
  ExternalMultimodalQueryResponse,
  ImageQueryFilters,
  ImageSearchType,
} from '@scale/mm-shared';
import { ReviewLevel } from '@scale/shared-types';
import { Attachment } from '@scale/shared/types/chat';
import { MultimodalImageServiceEventType } from '@scale/shared/types/events/MultimodalImageServiceEvent';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import apiFetch, { handleApiFetchError, HTTPError } from 'react-scale/lib/api-fetch';
import { Event, logEvent } from 'react-scale/lib/events';
import { MultimodalPromptSuggestionEventType } from '@scale/shared/types/events/MultimodalPromptSuggestionEvent';
import { useMutation } from 'react-query';

export const IMAGES_PER_PAGE = 24;
export const MAX_PAGES = 10;

export type MultimodalImageServiceHook = ReturnType<typeof useMultimodalImageService>;

export interface UseMultimodalImageServiceProps {
  projectId?: string;
  assignmentId?: string;
  configVariables: Record<string, string>;
  reviewLevel: ReviewLevel;
  startTime: Date | null;
}

export const useMultimodalImageService = ({
  projectId,
  assignmentId,
  configVariables,
  reviewLevel,
  startTime,
}: UseMultimodalImageServiceProps) => {
  const [images, setImages] = useState<MultimodalAssetDto[]>([]);
  const [numHits, setNumHits] = useState(0);
  const [canPreloadImages, setCanPreloadImages] = useState<boolean>(true);
  const [preloadedImagesQuery, setPreloadedImagesQuery] = useState<string>('');
  const searchType = ImageSearchType.LEXICAL;

  const [promptSuggestions, setPromptSuggestions] = useState<string[] | undefined>(undefined);

  const searchStartTime = startTime?.getTime() ?? Date.now();

  const fetchImagesWithFilters = async (filters: ImageQueryFilters) => {
    try {
      const result: ExternalMultimodalQueryResponse = await apiFetch(
        null,
        '/internal/multimodal-images/query',
        {
          method: 'POST',
          body: filters,
        },
      );

      setNumHits(result.num_hits);
      setImages(_.uniqBy(result.assets, 'image_url'));
    } catch (e) {
      handleApiFetchError(e);
    }
  };

  const fetchSimilarImagesByEmbedding = async (
    imageUrl: string,
    numResults: number,
  ): Promise<void> => {
    try {
      const result: MultimodalAssetDto[] = await apiFetch(
        null,
        '/internal/multimodal-images/similar_images',
        {
          method: 'GET',
          query: { imageUrl, numResults: numResults.toString() },
        },
      );

      setImages(_.uniqBy(result, 'image_url'));
    } catch (e) {
      handleApiFetchError(e);
    }
  };

  const incrementClickedOn = async (imageUrl: string): Promise<void> => {
    try {
      await apiFetch(null, '/internal/multimodal-images/increment_clicked_on', {
        method: 'POST',
        body: { imageUrl },
      });
    } catch (e) {
      handleApiFetchError(e);
    }
  };

  const flagLowQuality = async (imageUrl: string): Promise<void> => {
    try {
      await apiFetch(null, '/internal/multimodal-images/flag_low_quality', {
        method: 'POST',
        body: { imageUrl },
      });
    } catch (e) {
      handleApiFetchError(e);
    }
  };

  const { mutateAsync: fetchPromptSuggestions, isLoading: isFetchingPromptSuggestions } =
    useMutation(
      async (imageUrl: string): Promise<string[]> => {
        const generatedPromptSuggestions: string[] = await apiFetch(
          null,
          '/internal/multimodal-images/prompt_suggestions',
          {
            method: 'GET',
            query: { imageUrl },
          },
        );
        setPromptSuggestions(generatedPromptSuggestions);
        return generatedPromptSuggestions;
      },
      {
        retry: 3,
        onSuccess: async (generatedPromptSuggestions: string[], imageUrl: string) => {
          await logEvent(Event.MultimodalPromptSuggestionEvent, {
            type: MultimodalPromptSuggestionEventType.PROMPT_SUGGESTION_RESULT,
            projectId,
            assignmentId,
            imageUrl,
            generationSucceded: true,
            promptSuggestions: generatedPromptSuggestions,
          });
        },
        onError: async (e: HTTPError, imageUrl: string) => {
          await logEvent(Event.MultimodalPromptSuggestionEvent, {
            type: MultimodalPromptSuggestionEventType.PROMPT_SUGGESTION_RESULT,
            projectId,
            assignmentId,
            imageUrl,
            generationSucceded: false,
          });
          handleApiFetchError(e);
        },
      },
    );

  const { mutateAsync: claimImage, isLoading: isClaimingImage } = useMutation(
    async (imageUrl: string): Promise<{ found: boolean }> => {
      const encodedImageUrl = encodeURIComponent(imageUrl);
      const response = await apiFetch(null, '/internal/multimodal-images/claim_image', {
        method: 'POST',
        query: { imageUrl: encodedImageUrl, projectId },
      });
      return response;
    },
    {
      onError: async (e: HTTPError) => {
        handleApiFetchError(e);
      },
    },
  );

  const releaseImageClaim = async (imageUrl: string): Promise<void> => {
    const encodedImageUrl = encodeURIComponent(imageUrl);
    try {
      await apiFetch(null, '/internal/multimodal-images/release_image_claim', {
        method: 'POST',
        query: { imageUrl: encodedImageUrl, projectId },
      });
    } catch (e) {
      handleApiFetchError(e);
    }
  };

  // Prefetch images from config variables //
  useEffect(() => {
    if (projectId && canPreloadImages) {
      if (!configVariables.taskSpecialization || !configVariables.category) {
        return;
      }
      const queryText = `${configVariables.taskSpecialization ?? ''} ${
        configVariables.category ?? ''
      } images`;

      fetchImagesWithFilters({
        numResults: 24,
        projectId,
        queryText,
        searchType: ImageSearchType.CLIP,
      });
      setPreloadedImagesQuery(queryText);
      setCanPreloadImages(false);
    }
  }, [projectId, configVariables, preloadedImagesQuery, canPreloadImages]);

  // LOGGING //

  const [lastUsedPage, setLastUsedPage] = useState<number>(1);
  const [timeToSearch, setTimeToSearch] = useState<number>(0);
  const logPromptInputEvent = async (
    projectId: string,
    attachment: Attachment,
    startTime: Date | null,
    assignmentId?: string,
  ) => {
    const timeFromAttachmentToInput = startTime ? Date.now() - startTime.getTime() : 0;
    await logEvent(Event.MultimodalImageServiceEvent, {
      type: MultimodalImageServiceEventType.CHAT_INPUT_SUBMISSION,
      assignmentId,
      projectId,
      imageSubmissionMethod: attachment.imageSubmissionMethod ?? undefined,
      lastUsedPage,
      timeToSearch,
      timeFromAttachmentToInput,
      searchType,
    });
  };

  return {
    images,
    setImages,
    numHits,
    fetchImagesWithFilters,
    fetchSimilarImagesByEmbedding,
    incrementClickedOn,
    flagLowQuality,
    promptSuggestions,
    setPromptSuggestions,
    fetchPromptSuggestions,
    isFetchingPromptSuggestions,
    lastUsedPage,
    setLastUsedPage,
    logPromptInputEvent,
    searchStartTime,
    timeToSearch,
    setTimeToSearch,
    setPreloadedImagesQuery,
    preloadedImagesQuery,
    searchType,
    reviewLevel,
    claimImage,
    isClaimingImage,
    releaseImageClaim,
  };
};

export const getDBMetrics = async (projectId?: string) => {
  try {
    const result = await apiFetch(null, '/internal/multimodal-images/get_db_metrics', {
      method: 'POST',
      body: { projectId },
    });

    return result;
  } catch (e) {
    handleApiFetchError(e);
  }
};

export default useMultimodalImageService;
